import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import {
  getProductFallbackImages,
  hashPick,
  placeholdProductImage,
} from "../lib/productImageFallbacks";

type Phase = "primary" | "fb0" | "fb1" | "fb2" | "placehold";

type Props = {
  id: number;
  name: string;
  imageUrl?: string | null;
  className?: string;
  style?: CSSProperties;
  alt?: string;
};

/**
 * Muestra la imagen del producto; si la URL falla, prueba respaldos temáticos y al final un placeholder con el nombre.
 */
export function ProductImage({
  id,
  name,
  imageUrl,
  className,
  style,
  alt,
}: Props) {
  const fallbackImages = useMemo(() => getProductFallbackImages(name), [name]);

  const baseIndex = useMemo(
    () => hashPick(id, name, fallbackImages.length),
    [id, name, fallbackImages.length],
  );

  const orderedFallbacks = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < fallbackImages.length; i++) {
      out.push(fallbackImages[(baseIndex + i) % fallbackImages.length]);
    }
    return out;
  }, [baseIndex, fallbackImages]);

  const [phase, setPhase] = useState<Phase>(() =>
    imageUrl && imageUrl.trim() ? "primary" : "fb0",
  );

  useEffect(() => {
    if (imageUrl?.trim()) setPhase("primary");
    else setPhase("fb0");
  }, [imageUrl]);

  const src = useMemo(() => {
    if (phase === "primary" && imageUrl?.trim()) return imageUrl.trim();
    if (phase === "fb0") return orderedFallbacks[0]!;
    if (phase === "fb1") return orderedFallbacks[1] ?? orderedFallbacks[0]!;
    if (phase === "fb2") return orderedFallbacks[2] ?? orderedFallbacks[0]!;
    return placeholdProductImage(name);
  }, [phase, imageUrl, orderedFallbacks, name]);

  const onError = () => {
    setPhase((p) => {
      if (p === "primary") return "fb0";
      if (p === "fb0") return "fb1";
      if (p === "fb1") return "fb2";
      return "placehold";
    });
  };

  return (
    <img
      src={src}
      className={className}
      style={style}
      alt={alt ?? name}
      onError={onError}
      loading="lazy"
      decoding="async"
    />
  );
}
